import { database } from '../firebase/firebase';
import { ref, get, set, update, push, query, orderByChild, equalTo } from 'firebase/database';
import { systemEventService } from './systemEventService';
import { qrCodeService } from './qrCodeService';

export const certificationService = {
  // Get all certifications
  getAllCertifications: async () => {
    try {
      const certificationsRef = ref(database, 'certifications');
      const snapshot = await get(certificationsRef);
      
      if (!snapshot.exists()) {
        return [];
      }

      const certificationsData = snapshot.val();
      return Object.keys(certificationsData).map(key => ({
        id: key,
        ...certificationsData[key]
      }));
    } catch (error) {
      console.error("Error fetching certifications:", error);
      throw error;
    }
  },

  // Get certifications by certifier ID
  getCertificationsByCertifierId: async (certifierId) => {
    try {
      const allCertifications = await certificationService.getAllCertifications();
      return allCertifications.filter(cert => 
        cert.certification_certifier_id === certifierId && 
        cert.certification_status === 'Approved'
      );
    } catch (error) {
      console.error("Error fetching certifications by certifier ID:", error);
      throw error;
    }
  },

  // Get approved certifications for current certifier
  getApprovedCertificationsByCurrentCertifier: async (currentUserId) => {
    try {
    
      const allCertifications = await certificationService.getAllCertifications();
      const approvedCertifications = allCertifications.filter(cert => 
        cert.certification_certifier_id === currentUserId && 
        cert.certification_status === 'Approved' || 'Rejected'
      );

       return approvedCertifications;
    } catch (error) {
      console.error("Error fetching approved certifications:", error);
      throw error;
    }
  },

  // Get certification by ID
  getCertificationById: async (certificationId) => {
    try {
      const certificationRef = ref(database, `certifications/${certificationId}`);
      const snapshot = await get(certificationRef);
      
      if (!snapshot.exists()) {
        return null;
      }

      return {
        id: certificationId,
        ...snapshot.val()
      };
    } catch (error) {
      console.error("Error fetching certification by ID:", error);
      throw error;
    }
  },

  // Get pending certifications
  getPendingCertifications: async () => {
    try {
      const allCertifications = await certificationService.getAllCertifications();
      return allCertifications.filter(cert => cert.certification_status === 'Pending');
    } catch (error) {
      console.error("Error fetching pending certifications:", error);
      throw error;
    }
  },

  // Create a new certification
  createCertification: async (productId) => {
    try {
      const allCertifications = await certificationService.getAllCertifications();

      // Check if certification already exists for this product
      const existingCertification = allCertifications.find(
        cert => cert.certification_product_id === productId
      );

      if (existingCertification) {
        console.log('Using existing certification');
        return existingCertification.id;
      }

      // Create new certification
      const certificationId = Date.now().toString();
      const certificationNumber = certificationService.generateCertificationNumber();

      const certificationData = {
        certification_product_id: productId,
        certification_certifier_id: '',
        certification_status: 'Pending',
        certification_issue_date: '',
        certification_created_at: Date.now(),
        certification_expiry_date: '',
        certification_pdf_url: 'certification_pdf_url base 64',
        certification_qr_code: '',
        certification_notes: '',
        certification_body: '',
        certification_number: certificationNumber,
        certification_updated_at: Date.now(),
        certification_certificate_version: 0
      };

      await set(ref(database, `certifications/${certificationId}`), certificationData);
      console.log('New certification created');
      return certificationId;

    } catch (error) {
      console.error('Error creating certification:', error);
      throw error;
    }
  },

  // Approve certification
  approveCertification: async (certificationId, product, currentUser, certificationData) => {
    try {
      const { certificationBody, certificationExpiryDate, certificationNotes } = certificationData;
      
      if (!certificationExpiryDate || !certificationBody) {
        throw new Error('Expiry date and certifying body are required');
      }

      // Generate QR codes
      const productQRCode = await qrCodeService.generateQRCode('products', product.id);
      const certificationQRCode = await qrCodeService.generateQRCode('certifications', certificationId);

      // Update certification
      const certRef = ref(database, `certifications/${certificationId}`);
      const snapshot = await get(certRef);
      const currentCert = snapshot.val();
      const currentVersion = currentCert.certification_certificate_version || 0;

      const certificationUpdates = {
        certification_status: 'Approved',
        certification_certifier_id: currentUser.uid,
        certification_issue_date: new Date().toISOString(),
        certification_expiry_date: certificationExpiryDate,
        certification_notes: certificationNotes,
        certification_body: certificationBody,
        certification_qr_code: certificationQRCode,
        certification_certificate_version: currentVersion + 1,
        certification_updated_at: Date.now(),
      };

      await update(certRef, certificationUpdates);

      // Update product status
      const productRef = ref(database, `products/${product.id}`);
      await update(productRef, {
        product_certification_status: 'Approved',
        product_qr_code: productQRCode,
        product_updated_at: Date.now(),
      });

      // Create notification
      const notificationsRef = ref(database, 'notifications');
      const currentDate = new Date().toISOString();
      const expirationDate = new Date(currentDate);
      expirationDate.setDate(expirationDate.getDate() + 30);

      await push(notificationsRef, {
        notification_user_id: product.product_factory_id,
        notification_title: "Product approved",
        notification_message: `Your product "${product.product_name}" has been approved.`,
        notification_type: "success",
        notification_is_read: false,
        notification_action_url: '/Edit_Products',
        notification_priority: "Low",
        notification_created_at: currentDate,
        notification_expires_at: expirationDate.toISOString()
      });

      // Log system event
      await systemEventService.logSystemEvent({
        event_type: 'New Product Certified',
        event_details: `Product "${product.product_name}" certified`,
        event_admin_id: currentUser.uid,
        event_related_id: product.id
      });

      return { success: true };
    } catch (error) {
      console.error('Error approving certification:', error);
      throw error;
    }
  },

  // Reject certification
  rejectCertification: async (certificationId, product, currentUser, rejectionData) => {
    try {
      const { rejectionReason, certificationBody, certificationNotes } = rejectionData;
      
      if (!rejectionReason) {
        throw new Error('Rejection reason is required');
      }

      // Update certification
      const certRef = ref(database, `certifications/${certificationId}`);
      const snapshot = await get(certRef);
      const currentCert = snapshot.val();
      const currentVersion = currentCert.certification_certificate_version || 0;

      const certificationUpdates = {
        certification_status: 'Rejected',
        certification_certifier_id: currentUser.uid,
        certification_notes: rejectionReason,
        certification_certificate_version: currentVersion + 1,
        certification_updated_at: Date.now(),
        certification_body: certificationBody,
      };

      await update(certRef, certificationUpdates);

      // Update product status
      const productRef = ref(database, `products/${product.id}`);
      await update(productRef, {
        product_certification_status: 'Rejected',
        product_updated_at: Date.now(),
      });

      // Create notification
      const notificationsRef = ref(database, 'notifications');
      const currentDate = new Date().toISOString();
      const expirationDate = new Date(currentDate);
      expirationDate.setDate(expirationDate.getDate() + 30);

      await push(notificationsRef, {
        notification_user_id: product.product_factory_id,
        notification_title: "Product Rejected",
        notification_message: `Your product "${product.product_name}" was rejected: ${rejectionReason}`,
        notification_type: "error",
        notification_is_read: false,
        notification_action_url: '/Edit_Products',
        notification_priority: "Low",
        notification_created_at: currentDate,
        notification_expires_at: expirationDate.toISOString()
      });

      // Log system event
      await systemEventService.logSystemEvent({
        event_type: 'Certification Delisted',
        event_details: `Product "${product.product_name}" rejected: ${rejectionReason}`,
        event_admin_id: currentUser.uid,
        event_related_id: product.id
      });

      return { success: true };
    } catch (error) {
      console.error('Error rejecting certification:', error);
      throw error;
    }
  },

  // Generate certification number
  generateCertificationNumber: () => {
    return `${Date.now()}`;
  }, 

  // Get certification by product ID
  getCertificationByProductId: async (productId) => {
    try {
      const allCertifications = await certificationService.getAllCertifications();
      return allCertifications.find(cert => cert.certification_product_id === productId);
    } catch (error) {
      console.error('Error fetching certification by product ID:', error);
      throw error;
    }
  }
};